import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import PortalTopbar from "../components/PortalTopbar";
import PortalAlert from "../components/PortalAlert";
import "../components/PortalButton.css";
import "./RequestAccessPage.css";

type ClinicAdminAction = "Add" | "Replace";
type ClinicExistsChoice = "Existing" | "New";

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; accessRequestId: number }
  | { kind: "error"; message: string };

type ClinicPickerItem = {
  clinicId: number;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  cityName: string | null;
  postalCode: string | null;
  activeAdminCount: number;
};

type CityItem = {
  cityId: number;
  name: string;
  regionId: number | null;
};

function asText(v: string): string {
  return String(v || "").trim();
}

function clinicLabel(c: ClinicPickerItem): string {
  const parts: string[] = [];
  if (c.name) parts.push(c.name);
  const addr = [c.addressLine1, c.addressLine2].filter(Boolean).join(", ");
  const cityPostal = [c.cityName || "", c.postalCode ? `(${c.postalCode})` : ""]
    .filter(Boolean)
    .join(" ");
  const tail = [addr, cityPostal].filter(Boolean).join(" · ");
  if (tail) parts.push(tail);
  return parts.join(" — ");
}

function normalizeClinicsResponse(data: any): ClinicPickerItem[] {
  const raw: any[] = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.clinics)
    ? data.clinics
    : Array.isArray(data)
    ? data
    : [];

  return raw
    .map((x: any) => ({
      clinicId: Number(x?.clinicId ?? x?.ClinicID ?? 0),
      name: String(x?.name ?? x?.Name ?? "").trim(),
      addressLine1: (x?.addressLine1 ?? x?.AddressLine1 ?? null) as string | null,
      addressLine2: (x?.addressLine2 ?? x?.AddressLine2 ?? null) as string | null,
      cityName: (x?.cityName ?? x?.CityName ?? null) as string | null,
      postalCode: (x?.postalCode ?? x?.PostalCode ?? null) as string | null,
      activeAdminCount: Math.max(
        0,
        Number(
          x?.activeAdminCount ??
            x?.ActiveAdminCount ??
            x?.active_admin_count ??
            0
        ) || 0
      ),
    }))
    .filter((x) => Number.isFinite(x.clinicId) && x.clinicId > 0 && x.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeCitiesResponse(data: any): CityItem[] {
  const raw: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : [];
  return raw
    .map((x: any) => ({
      cityId: Number(x?.cityId ?? x?.CityID ?? 0),
      name: String(x?.name ?? x?.Name ?? "").trim(),
      regionId:
        x?.regionId !== undefined && x?.regionId !== null
          ? Number(x.regionId)
          : x?.RegionID !== undefined && x?.RegionID !== null
          ? Number(x.RegionID)
          : null,
    }))
    .filter((c) => Number.isFinite(c.cityId) && c.cityId > 0 && c.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function findExactCityMatch(items: CityItem[], value: string): CityItem | null {
  const q = asText(value).toLowerCase();
  if (!q) return null;
  const matches = items.filter((c) => c.name.toLowerCase() === q);
  return matches.length === 1 ? matches[0] : null;
}

function formatNorthAmericanPhone(raw: string): string {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidNorthAmericanPhone(val: string): boolean {
  return /^\(\d{3}\) \d{3}-\d{4}$/.test(val);
}

function formatCanadianPostal(raw: string): string {
  const cleaned = String(raw || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 6);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
}

function isValidCanadianPostal(val: string): boolean {
  const s = String(val || "").trim().toUpperCase();
  return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] \d[ABCEGHJ-NPRSTV-Z]\d$/.test(s);
}

const TERMS_URL = (process.env.REACT_APP_TERMS_URL as string) || "/public/terms";
const PRIVACY_URL =
  (process.env.REACT_APP_PRIVACY_URL as string) || "/public/privacy";
const CLINIC_ADMIN_AGREEMENT_URL =
  (process.env.REACT_APP_CLINIC_ADMIN_AGREEMENT_URL as string) ||
  "/public/legal/clinic-admin-agreement";

export default function RequestAccessPage() {
  const nav = useNavigate();
  const formId = useId();
  const id = useCallback((key: string) => `${formId}-${key}`, [formId]);

  const [clinicExistsChoice, setClinicExistsChoice] =
    useState<ClinicExistsChoice>("Existing");
  const [clinicAdminAction, setClinicAdminAction] =
    useState<ClinicAdminAction>("Add");

  const [requesterFirstName, setRequesterFirstName] = useState("");
  const [requesterLastName, setRequesterLastName] = useState("");
  const [requesterPhone, setRequesterPhone] = useState("");

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [clinicAdminAgreementAccepted, setClinicAdminAgreementAccepted] =
    useState(false);
  const [attestationAccepted, setAttestationAccepted] = useState(false);

  const [clinics, setClinics] = useState<ClinicPickerItem[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinicsError, setClinicsError] = useState<string | null>(null);

  const [clinicSearch, setClinicSearch] = useState("");
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clinicCursor, setClinicCursor] = useState(-1);
  const pickerWrapRef = useRef<HTMLDivElement | null>(null);

  const [newClinicName, setNewClinicName] = useState("");
  const [newClinicAddressLine1, setNewClinicAddressLine1] = useState("");
  const [newClinicAddressLine2, setNewClinicAddressLine2] = useState("");
  const [newClinicPostalCode, setNewClinicPostalCode] = useState("");
  const [newClinicPhone, setNewClinicPhone] = useState("");
  const [newClinicWebsite, setNewClinicWebsite] = useState("");

  const [cities, setCities] = useState<CityItem[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesError, setCitiesError] = useState<string | null>(null);

  const [citySearch, setCitySearch] = useState("");
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [cityCursor, setCityCursor] = useState(-1);
  const cityPickerWrapRef = useRef<HTMLDivElement | null>(null);

  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  const selectedClinic = useMemo(
    () =>
      selectedClinicId
        ? clinics.find((c) => c.clinicId === selectedClinicId) || null
        : null,
    [clinics, selectedClinicId]
  );

  const selectedCity = useMemo(
    () =>
      selectedCityId
        ? cities.find((c) => c.cityId === selectedCityId) || null
        : null,
    [cities, selectedCityId]
  );

  const selectedClinicRequiresAction = useMemo(() => {
    if (clinicExistsChoice !== "Existing" || !selectedClinic) return false;
    return Number(selectedClinic.activeAdminCount || 0) >= 1;
  }, [clinicExistsChoice, selectedClinic]);

  const loadClinics = useCallback(async () => {
    setClinicsLoading(true);
    setClinicsError(null);
    try {
      const res = await fetch("/api/request-access-clinics", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          data?.error || data?.message || `Failed to load clinics (${res.status})`;
        throw new Error(msg);
      }
      const mapped = normalizeClinicsResponse(data);
      setClinics(mapped);
    } catch (e: any) {
      setClinics([]);
      setSelectedClinicId(null);
      setClinicsError(e?.message || "Failed to load clinics.");
    } finally {
      setClinicsLoading(false);
    }
  }, []);

  const loadCities = useCallback(async () => {
    setCitiesLoading(true);
    setCitiesError(null);
    try {
      const res = await fetch("/api/cities", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          data?.error || data?.message || `Failed to load cities (${res.status})`;
        throw new Error(msg);
      }
      setCities(normalizeCitiesResponse(data));
    } catch (e: any) {
      setCities([]);
      setSelectedCityId(null);
      setCitiesError(e?.message || "Failed to load cities.");
    } finally {
      setCitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (clinicExistsChoice === "Existing") loadClinics();
  }, [clinicExistsChoice, loadClinics]);

  useEffect(() => {
    if (clinicExistsChoice === "New") loadCities();
  }, [clinicExistsChoice, loadCities]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const el = pickerWrapRef.current;
      if (el && !el.contains(e.target as Node)) setPickerOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const el = cityPickerWrapRef.current;
      if (el && !el.contains(e.target as Node)) {
        const exactMatch = findExactCityMatch(cities, citySearch);
        if (exactMatch) {
          setSelectedCityId(exactMatch.cityId);
          setCitySearch("");
          setState({ kind: "idle" });
        }
        setCityPickerOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [cities, citySearch]);

  const filteredClinics = useMemo(() => {
    const q = asText(clinicSearch).toLowerCase();
    if (q.length < 2) return [];
    return clinics
      .map((c) => {
        const hay = `${c.name} ${c.addressLine1 || ""} ${c.cityName || ""} ${
          c.postalCode || ""
        }`.toLowerCase();
        return { c, idx: hay.indexOf(q) };
      })
      .filter((x) => x.idx >= 0)
      .sort((a, b) => a.idx - b.idx)
      .slice(0, 8)
      .map((x) => x.c);
  }, [clinics, clinicSearch]);

  const filteredCities = useMemo(() => {
    const q = asText(citySearch).toLowerCase();
    if (q.length < 2) return [];
    return cities
      .map((c) => ({ c, idx: c.name.toLowerCase().indexOf(q) }))
      .filter((x) => x.idx >= 0)
      .sort((a, b) => a.idx - b.idx)
      .slice(0, 10)
      .map((x) => x.c);
  }, [cities, citySearch]);

  useEffect(() => {
    if (clinicCursor >= filteredClinics.length) setClinicCursor(-1);
  }, [filteredClinics.length, clinicCursor]);

  useEffect(() => {
    if (cityCursor >= filteredCities.length) setCityCursor(-1);
  }, [filteredCities.length, cityCursor]);

  const requesterPhoneInvalid =
    asText(requesterPhone).length > 0 &&
    !isValidNorthAmericanPhone(asText(requesterPhone));

  const newClinicPhoneInvalid =
    clinicExistsChoice === "New" &&
    asText(newClinicPhone).length > 0 &&
    !isValidNorthAmericanPhone(asText(newClinicPhone));

  const postalInvalid =
    clinicExistsChoice === "New" &&
    asText(newClinicPostalCode).length > 0 &&
    !isValidCanadianPostal(asText(newClinicPostalCode).toUpperCase());

  const canSubmit = useMemo(() => {
    if (state.kind === "submitting") return false;
    const fn = asText(requesterFirstName);
    const ln = asText(requesterLastName);
    const rp = asText(requesterPhone);
    if (!fn || !ln || !rp) return false;
    if (!isValidNorthAmericanPhone(rp)) return false;
    if (
      !attestationAccepted ||
      !termsAccepted ||
      !privacyAccepted ||
      !clinicAdminAgreementAccepted
    )
      return false;

    if (clinicExistsChoice === "Existing") {
      if (!selectedClinicId) return false;
      if (selectedClinicRequiresAction) {
        if (clinicAdminAction !== "Add" && clinicAdminAction !== "Replace")
          return false;
      }
      return true;
    }

    const name = asText(newClinicName);
    const addr1 = asText(newClinicAddressLine1);
    const postal = asText(newClinicPostalCode).toUpperCase();
    const phone = asText(newClinicPhone);
    if (!name || !addr1 || !selectedCityId || !postal || !phone) return false;
    if (!isValidCanadianPostal(postal)) return false;
    if (!isValidNorthAmericanPhone(phone)) return false;
    return true;
  }, [
    state.kind,
    requesterFirstName,
    requesterLastName,
    requesterPhone,
    attestationAccepted,
    termsAccepted,
    privacyAccepted,
    clinicAdminAgreementAccepted,
    clinicExistsChoice,
    selectedClinicId,
    selectedClinicRequiresAction,
    clinicAdminAction,
    newClinicName,
    newClinicAddressLine1,
    selectedCityId,
    newClinicPostalCode,
    newClinicPhone,
  ]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ kind: "submitting" });

    const payload: any = {
      attestationAccepted: true,
      requesterFirstName: asText(requesterFirstName),
      requesterLastName: asText(requesterLastName),
      requesterPhone: asText(requesterPhone),
      termsAccepted: true,
      privacyAccepted: true,
      clinicAdminAgreementAccepted: true,
    };

    if (clinicExistsChoice === "Existing") {
      payload.clinicId = selectedClinicId;
      if (selectedClinicRequiresAction) {
        payload.clinicAdminAction = clinicAdminAction;
      }
    } else {
      payload.newClinicName = asText(newClinicName);
      payload.newClinicAddressLine1 = asText(newClinicAddressLine1);
      payload.newClinicAddressLine2 = asText(newClinicAddressLine2) || null;
      payload.newClinicCityId = selectedCityId;
      payload.newClinicPostalCode = asText(newClinicPostalCode).toUpperCase();
      payload.newClinicPhone = asText(newClinicPhone);
      payload.newClinicWebsite = asText(newClinicWebsite) || null;
    }

    try {
      const res = await fetch("/api/access-requests-submit", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (res.status === 201) {
        setState({
          kind: "success",
          accessRequestId: Number(data?.accessRequestId || 0),
        });
        return;
      }
      if (res.status === 409) {
        setState({
          kind: "error",
          message:
            data?.error ||
            "A pending Clinic Admin request already exists for this scope. Please wait for a decision.",
        });
        return;
      }
      if (res.status === 401) {
        setState({
          kind: "error",
          message: "You are not signed in. Please sign in and try again.",
        });
        return;
      }
      setState({
        kind: "error",
        message:
          data?.error || data?.message || "Request failed. Please try again.",
      });
    } catch (err: any) {
      setState({
        kind: "error",
        message: err?.message || "Request failed. Please try again.",
      });
    }
  }

  function onPickClinic(c: ClinicPickerItem) {
    setSelectedClinicId(c.clinicId);
    setClinicSearch("");
    setPickerOpen(false);
    setClinicCursor(-1);
    setState({ kind: "idle" });
  }

  function clearSelectedClinic() {
    setSelectedClinicId(null);
    setClinicSearch("");
    setPickerOpen(false);
    setClinicCursor(-1);
    setState({ kind: "idle" });
  }

  function onPickCity(c: CityItem) {
    setSelectedCityId(c.cityId);
    setCitySearch("");
    setCityPickerOpen(false);
    setCityCursor(-1);
    setState({ kind: "idle" });
  }

  function clearSelectedCity() {
    setSelectedCityId(null);
    setCitySearch("");
    setCityPickerOpen(false);
    setCityCursor(-1);
    setState({ kind: "idle" });
  }

  function onClinicKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!pickerOpen || filteredClinics.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setClinicCursor((c) => (c + 1) % filteredClinics.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setClinicCursor((c) =>
        c <= 0 ? filteredClinics.length - 1 : c - 1
      );
    } else if (e.key === "Enter") {
      if (clinicCursor >= 0 && clinicCursor < filteredClinics.length) {
        e.preventDefault();
        onPickClinic(filteredClinics[clinicCursor]);
      }
    } else if (e.key === "Escape") {
      setPickerOpen(false);
    }
  }

  function onCityKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!cityPickerOpen || filteredCities.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCityCursor((c) => (c + 1) % filteredCities.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCityCursor((c) => (c <= 0 ? filteredCities.length - 1 : c - 1));
    } else if (e.key === "Enter") {
      if (cityCursor >= 0 && cityCursor < filteredCities.length) {
        e.preventDefault();
        onPickCity(filteredCities[cityCursor]);
      }
    } else if (e.key === "Escape") {
      setCityPickerOpen(false);
    }
  }

  const showClinicSuggestions =
    clinicExistsChoice === "Existing" &&
    !selectedClinicId &&
    pickerOpen &&
    asText(clinicSearch).length >= 2 &&
    !clinicsError &&
    !clinicsLoading;

  const showCitySuggestions =
    clinicExistsChoice === "New" &&
    !selectedCityId &&
    cityPickerOpen &&
    asText(citySearch).length >= 2 &&
    !citiesError &&
    !citiesLoading;

  const allLegalChecked =
    attestationAccepted &&
    termsAccepted &&
    privacyAccepted &&
    clinicAdminAgreementAccepted;

  return (
    <div className="rav-shell">
      <PortalTopbar showSignOut showPortalLogin={false} />

      <main className="rav-main" id="main">
        <div className="rav-card">
          <header className="rav-header">
            <h1 className="rav-title">Request Clinic Admin access</h1>
            <p className="rav-subtitle">
              This request is reviewed manually. We may contact the clinic to
              verify details before granting access.
            </p>
          </header>

          <form onSubmit={onSubmit} className="rav-form" noValidate>
            {/* ---- Requester identity ---- */}
            <section className="rav-section" aria-labelledby={id("info-title")}>
              <h2 id={id("info-title")} className="rav-section-title">
                Your information
              </h2>

              <div className="rav-grid rav-grid--two">
                <div className="rav-field">
                  <label htmlFor={id("first")} className="rav-label">
                    First name <span className="rav-required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id={id("first")}
                    className="rav-input"
                    value={requesterFirstName}
                    onChange={(e) => setRequesterFirstName(e.target.value)}
                    autoComplete="given-name"
                    aria-required="true"
                    required
                  />
                </div>

                <div className="rav-field">
                  <label htmlFor={id("last")} className="rav-label">
                    Last name <span className="rav-required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id={id("last")}
                    className="rav-input"
                    value={requesterLastName}
                    onChange={(e) => setRequesterLastName(e.target.value)}
                    autoComplete="family-name"
                    aria-required="true"
                    required
                  />
                </div>
              </div>

              <div className="rav-field">
                <label htmlFor={id("phone")} className="rav-label">
                  Personal phone number <span className="rav-required" aria-hidden="true">*</span>
                </label>
                <input
                  id={id("phone")}
                  className="rav-input"
                  value={requesterPhone}
                  onChange={(e) =>
                    setRequesterPhone(formatNorthAmericanPhone(e.target.value))
                  }
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={14}
                  aria-required="true"
                  aria-invalid={requesterPhoneInvalid}
                  aria-describedby={`${id("phone-help")} ${requesterPhoneInvalid ? id("phone-err") : ""}`.trim()}
                  required
                />
                <p id={id("phone-help")} className="rav-help">
                  Format: (905) 555-1234. Used for verification purposes only;
                  not shared publicly.
                </p>
                {requesterPhoneInvalid ? (
                  <p id={id("phone-err")} className="rav-error" role="alert">
                    Enter a valid phone number, e.g. (905) 555-1234.
                  </p>
                ) : null}
              </div>
            </section>

            {/* ---- Clinic-type chooser ---- */}
            <section className="rav-section" aria-labelledby={id("type-title")}>
              <h2 id={id("type-title")} className="rav-section-title">
                What would you like to do?
              </h2>

              <div
                className="rav-segmented"
                role="radiogroup"
                aria-labelledby={id("type-title")}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={clinicExistsChoice === "Existing"}
                  onClick={() => {
                    setClinicExistsChoice("Existing");
                    setState({ kind: "idle" });
                  }}
                  className={`rav-segbtn ${
                    clinicExistsChoice === "Existing" ? "rav-segbtn--active" : ""
                  }`}
                >
                  Join an existing clinic
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={clinicExistsChoice === "New"}
                  onClick={() => {
                    setClinicExistsChoice("New");
                    clearSelectedClinic();
                    clearSelectedCity();
                    setState({ kind: "idle" });
                  }}
                  className={`rav-segbtn ${
                    clinicExistsChoice === "New" ? "rav-segbtn--active" : ""
                  }`}
                >
                  Create a new clinic
                </button>
              </div>
              <p className="rav-help">
                If the clinic already exists in AcceptingMD, select it. Otherwise
                submit the clinic details to create it.
              </p>
            </section>

            {/* ---- Existing clinic picker ---- */}
            {clinicExistsChoice === "Existing" ? (
              <section className="rav-section" aria-labelledby={id("existing-title")}>
                <div className="rav-section-head">
                  <h2 id={id("existing-title")} className="rav-section-title">
                    Existing clinic
                  </h2>
                  <button
                    type="button"
                    onClick={loadClinics}
                    disabled={clinicsLoading}
                    className="pb-btn pb-btn--ghost"
                  >
                    {clinicsLoading ? (
                      <>
                        <span className="pb-spinner" aria-hidden="true" />
                        Refreshing
                      </>
                    ) : (
                      "Refresh clinics"
                    )}
                  </button>
                </div>

                <div className="rav-field" ref={pickerWrapRef}>
                  <label htmlFor={id("clinic-picker")} className="rav-label">
                    Find and select a clinic{" "}
                    <span className="rav-required" aria-hidden="true">*</span>
                  </label>

                  {selectedClinic ? (
                    <div className="rav-selected" aria-live="polite">
                      <div className="rav-selected-head">
                        <span className="rav-selected-eyebrow">Selected clinic</span>
                        <button
                          type="button"
                          className="pb-btn pb-btn--ghost"
                          onClick={clearSelectedClinic}
                        >
                          Change
                        </button>
                      </div>
                      <div className="rav-selected-detail">
                        {clinicLabel(selectedClinic)}
                      </div>
                    </div>
                  ) : (
                    <>
                      <input
                        id={id("clinic-picker")}
                        className="rav-input"
                        type="text"
                        role="combobox"
                        aria-expanded={showClinicSuggestions}
                        aria-autocomplete="list"
                        aria-controls={id("clinic-list")}
                        aria-activedescendant={
                          clinicCursor >= 0 && filteredClinics[clinicCursor]
                            ? `${id("clinic-opt")}-${filteredClinics[clinicCursor].clinicId}`
                            : undefined
                        }
                        value={clinicSearch}
                        onChange={(e) => {
                          setClinicSearch(e.target.value);
                          setPickerOpen(true);
                          setClinicCursor(-1);
                        }}
                        onFocus={() => setPickerOpen(true)}
                        onKeyDown={onClinicKeyDown}
                        aria-required="true"
                        aria-describedby={id("clinic-help")}
                        placeholder={clinicsLoading ? "Loading clinics" : ""}
                      />

                      <p id={id("clinic-help")} className="rav-help">
                        Type at least 2 characters. Matches on clinic name,
                        address, or city. Use arrow keys to navigate suggestions.
                      </p>

                      {clinicsError ? (
                        <PortalAlert tone="danger" title="Couldn't load clinics">
                          {clinicsError}. You can still create a new clinic
                          instead, or retry with the Refresh button.
                        </PortalAlert>
                      ) : null}

                      {showClinicSuggestions ? (
                        <ul
                          id={id("clinic-list")}
                          className="rav-suggest"
                          role="listbox"
                        >
                          {filteredClinics.length === 0 ? (
                            <li className="rav-suggest-empty">
                              No matches. Try another search or create a new clinic.
                            </li>
                          ) : (
                            filteredClinics.map((c, i) => (
                              <li
                                key={c.clinicId}
                                id={`${id("clinic-opt")}-${c.clinicId}`}
                                role="option"
                                aria-selected={i === clinicCursor}
                              >
                                <button
                                  type="button"
                                  onMouseEnter={() => setClinicCursor(i)}
                                  onClick={() => onPickClinic(c)}
                                  className={`rav-suggest-row ${
                                    i === clinicCursor
                                      ? "rav-suggest-row--active"
                                      : ""
                                  }`}
                                >
                                  <span className="rav-suggest-title">
                                    {c.name}
                                  </span>
                                  <span className="rav-suggest-sub">
                                    {clinicLabel(c)}
                                  </span>
                                </button>
                              </li>
                            ))
                          )}
                        </ul>
                      ) : null}
                    </>
                  )}
                </div>

                {/* Add/Replace gate */}
                <div className="rav-field">
                  {selectedClinic && selectedClinicRequiresAction ? (
                    <>
                      <label htmlFor={id("action")} className="rav-label">
                        Clinic Admin access{" "}
                        <span className="rav-required" aria-hidden="true">*</span>
                      </label>
                      <select
                        id={id("action")}
                        className="rav-input"
                        value={clinicAdminAction}
                        onChange={(e) =>
                          setClinicAdminAction(e.target.value as ClinicAdminAction)
                        }
                        aria-required="true"
                      >
                        <option value="Add">
                          Add me as an additional Clinic Admin
                        </option>
                        <option value="Replace">
                          Replace existing Clinic Admin(s)
                        </option>
                      </select>
                      <p className="rav-help">
                        Multiple clinic admins are allowed. Use{" "}
                        <strong>Replace</strong> only when you need to remove
                        existing Clinic Admin access.
                      </p>
                    </>
                  ) : selectedClinic ? (
                    <p className="rav-help">
                      This clinic currently has no active Clinic Admins. Your
                      request will assign you as the first Clinic Admin.
                    </p>
                  ) : (
                    <p className="rav-help">
                      Select a clinic above to continue.
                    </p>
                  )}
                </div>
              </section>
            ) : null}

            {/* ---- New clinic form ---- */}
            {clinicExistsChoice === "New" ? (
              <section className="rav-section" aria-labelledby={id("new-title")}>
                <h2 id={id("new-title")} className="rav-section-title">
                  New clinic information
                </h2>

                <div className="rav-field">
                  <label htmlFor={id("new-name")} className="rav-label">
                    Clinic name{" "}
                    <span className="rav-required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id={id("new-name")}
                    className="rav-input"
                    value={newClinicName}
                    onChange={(e) => setNewClinicName(e.target.value)}
                    aria-required="true"
                    required
                  />
                </div>

                <div className="rav-field">
                  <label htmlFor={id("new-addr1")} className="rav-label">
                    Clinic address line 1{" "}
                    <span className="rav-required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id={id("new-addr1")}
                    className="rav-input"
                    value={newClinicAddressLine1}
                    onChange={(e) => setNewClinicAddressLine1(e.target.value)}
                    autoComplete="address-line1"
                    aria-required="true"
                    required
                  />
                </div>

                <div className="rav-field">
                  <label htmlFor={id("new-addr2")} className="rav-label">
                    Clinic address line 2{" "}
                    <span className="rav-optional">(optional)</span>
                  </label>
                  <input
                    id={id("new-addr2")}
                    className="rav-input"
                    value={newClinicAddressLine2}
                    onChange={(e) => setNewClinicAddressLine2(e.target.value)}
                    autoComplete="address-line2"
                  />
                </div>

                <div className="rav-grid rav-grid--two">
                  <div className="rav-field" ref={cityPickerWrapRef}>
                    <label htmlFor={id("city")} className="rav-label">
                      City{" "}
                      <span className="rav-required" aria-hidden="true">*</span>
                    </label>

                    {selectedCity ? (
                      <div className="rav-selected" aria-live="polite">
                        <div className="rav-selected-head">
                          <span className="rav-selected-eyebrow">Selected city</span>
                          <button
                            type="button"
                            className="pb-btn pb-btn--ghost"
                            onClick={clearSelectedCity}
                          >
                            Change
                          </button>
                        </div>
                        <div className="rav-selected-detail">{selectedCity.name}</div>
                      </div>
                    ) : (
                      <>
                        <input
                          id={id("city")}
                          className="rav-input"
                          type="text"
                          role="combobox"
                          aria-expanded={showCitySuggestions}
                          aria-autocomplete="list"
                          aria-controls={id("city-list")}
                          aria-activedescendant={
                            cityCursor >= 0 && filteredCities[cityCursor]
                              ? `${id("city-opt")}-${filteredCities[cityCursor].cityId}`
                              : undefined
                          }
                          value={citySearch}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            const exactMatch = findExactCityMatch(
                              cities,
                              nextValue
                            );
                            if (exactMatch) {
                              setSelectedCityId(exactMatch.cityId);
                              setCitySearch("");
                              setCityPickerOpen(false);
                            } else {
                              setSelectedCityId(null);
                              setCitySearch(nextValue);
                              setCityPickerOpen(true);
                            }
                            setCityCursor(-1);
                            setState({ kind: "idle" });
                          }}
                          onFocus={() => setCityPickerOpen(true)}
                          onKeyDown={onCityKeyDown}
                          placeholder={citiesLoading ? "Loading cities" : ""}
                          aria-required="true"
                          aria-describedby={id("city-help")}
                        />

                        <p id={id("city-help")} className="rav-help">
                          Type at least 2 characters. Use arrow keys to navigate
                          suggestions.
                        </p>

                        {citiesError ? (
                          <PortalAlert tone="danger" title="Couldn't load cities">
                            {citiesError}
                          </PortalAlert>
                        ) : null}

                        {showCitySuggestions ? (
                          <ul
                            id={id("city-list")}
                            className="rav-suggest"
                            role="listbox"
                          >
                            {filteredCities.length === 0 ? (
                              <li className="rav-suggest-empty">
                                No matches. Try a different city name.
                              </li>
                            ) : (
                              filteredCities.map((c, i) => (
                                <li
                                  key={c.cityId}
                                  id={`${id("city-opt")}-${c.cityId}`}
                                  role="option"
                                  aria-selected={i === cityCursor}
                                >
                                  <button
                                    type="button"
                                    onMouseEnter={() => setCityCursor(i)}
                                    onClick={() => onPickCity(c)}
                                    className={`rav-suggest-row ${
                                      i === cityCursor
                                        ? "rav-suggest-row--active"
                                        : ""
                                    }`}
                                  >
                                    <span className="rav-suggest-title">
                                      {c.name}
                                    </span>
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div className="rav-field">
                    <label htmlFor={id("postal")} className="rav-label">
                      Postal code{" "}
                      <span className="rav-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id={id("postal")}
                      className="rav-input"
                      value={newClinicPostalCode}
                      onChange={(e) =>
                        setNewClinicPostalCode(formatCanadianPostal(e.target.value))
                      }
                      inputMode="text"
                      autoComplete="postal-code"
                      autoCapitalize="characters"
                      maxLength={7}
                      aria-required="true"
                      aria-invalid={postalInvalid}
                      aria-describedby={`${id("postal-help")} ${postalInvalid ? id("postal-err") : ""}`.trim()}
                      required
                    />
                    <p id={id("postal-help")} className="rav-help">
                      Format: A1A 1A1 (Canadian postal code).
                    </p>
                    {postalInvalid ? (
                      <p id={id("postal-err")} className="rav-error" role="alert">
                        Enter a valid postal code, e.g. L1X 2H5.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="rav-grid rav-grid--two">
                  <div className="rav-field">
                    <label htmlFor={id("cphone")} className="rav-label">
                      Clinic phone number{" "}
                      <span className="rav-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id={id("cphone")}
                      className="rav-input"
                      value={newClinicPhone}
                      onChange={(e) =>
                        setNewClinicPhone(formatNorthAmericanPhone(e.target.value))
                      }
                      inputMode="tel"
                      autoComplete="tel"
                      maxLength={14}
                      aria-required="true"
                      aria-invalid={newClinicPhoneInvalid}
                      aria-describedby={`${id("cphone-help")} ${newClinicPhoneInvalid ? id("cphone-err") : ""}`.trim()}
                      required
                    />
                    <p id={id("cphone-help")} className="rav-help">
                      This number is used to verify clinic details.
                    </p>
                    {newClinicPhoneInvalid ? (
                      <p id={id("cphone-err")} className="rav-error" role="alert">
                        Enter a valid phone number, e.g. (905) 555-1234.
                      </p>
                    ) : null}
                  </div>

                  <div className="rav-field">
                    <label htmlFor={id("website")} className="rav-label">
                      Website <span className="rav-optional">(optional)</span>
                    </label>
                    <input
                      id={id("website")}
                      className="rav-input"
                      value={newClinicWebsite}
                      onChange={(e) => setNewClinicWebsite(e.target.value)}
                      inputMode="url"
                      autoComplete="url"
                    />
                    <p className="rav-help">
                      Example: https://exampleclinic.ca
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            {/* ---- Authorization + legal ---- */}
            <section className="rav-section" aria-labelledby={id("legal-title")}>
              <h2 id={id("legal-title")} className="rav-section-title">
                Authorization confirmation
              </h2>
              <p className="rav-help rav-help--tight">
                All four confirmations are required to submit this request.
              </p>

              <label htmlFor={id("attest")} className="rav-check">
                <input
                  id={id("attest")}
                  type="checkbox"
                  checked={attestationAccepted}
                  onChange={(e) => setAttestationAccepted(e.target.checked)}
                  aria-required="true"
                />
                <span>
                  I confirm that I am authorized to act on behalf of this clinic
                  and request administrative access.{" "}
                  <span className="rav-required" aria-hidden="true">*</span>
                </span>
              </label>

              <label htmlFor={id("terms")} className="rav-check">
                <input
                  id={id("terms")}
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  aria-required="true"
                />
                <span>
                  I agree to the AcceptingMD{" "}
                  <a href={TERMS_URL} target="_blank" rel="noreferrer noopener">
                    Terms of Use
                  </a>
                  .{" "}
                  <span className="rav-required" aria-hidden="true">*</span>
                </span>
              </label>

              <label htmlFor={id("privacy")} className="rav-check">
                <input
                  id={id("privacy")}
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  aria-required="true"
                />
                <span>
                  I agree to the{" "}
                  <a href={PRIVACY_URL} target="_blank" rel="noreferrer noopener">
                    Privacy Policy
                  </a>
                  .{" "}
                  <span className="rav-required" aria-hidden="true">*</span>
                </span>
              </label>

              <label htmlFor={id("agreement")} className="rav-check">
                <input
                  id={id("agreement")}
                  type="checkbox"
                  checked={clinicAdminAgreementAccepted}
                  onChange={(e) =>
                    setClinicAdminAgreementAccepted(e.target.checked)
                  }
                  aria-required="true"
                />
                <span>
                  I agree to the{" "}
                  <a
                    href={CLINIC_ADMIN_AGREEMENT_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Clinic Admin Agreement
                  </a>
                  .{" "}
                  <span className="rav-required" aria-hidden="true">*</span>
                </span>
              </label>

              {!allLegalChecked ? (
                <p className="rav-help rav-help--tight">
                  Confirm authorization and agree to all four items before
                  submitting.
                </p>
              ) : null}
            </section>

            {state.kind === "error" ? (
              <PortalAlert tone="danger" title="We couldn't submit your request">
                {state.message}
              </PortalAlert>
            ) : null}

            {state.kind === "success" ? (
              <PortalAlert tone="success" title="Request submitted">
                Reference #{state.accessRequestId}. A Super Admin will review it
                shortly. You can check status on the pending page.
              </PortalAlert>
            ) : null}

            <div className="rav-actions">
              <button
                type="submit"
                disabled={!canSubmit}
                className="pb-btn pb-btn--primary pb-btn--lg"
              >
                {state.kind === "submitting" ? (
                  <>
                    <span className="pb-spinner" aria-hidden="true" />
                    Submitting
                  </>
                ) : (
                  "Submit request"
                )}
              </button>
              <button
                type="button"
                onClick={() => nav("/portal/no-access")}
                className="pb-btn pb-btn--secondary pb-btn--lg"
              >
                Back
              </button>
            </div>

            <div className="rav-footer">
              <Link to="/portal/access-pending" className="rav-text-link">
                Check pending status
              </Link>
              <span className="rav-footer-sep" aria-hidden="true">·</span>
              <Link to="/public" className="rav-text-link">
                Public site
              </Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
